import { Tag } from "@redotech/redo-common-model-tags-common/tag";
import { RedoCompactBadgeSwatch } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { Dropdown } from "@redotech/redo-web/dropdown";
import { Flex } from "@redotech/redo-web/flex";
import { PillTheme } from "@redotech/redo-web/pill";
import {
  BadgePillThemeToColorMapping,
  BadgePillThemeToTypeMapping,
  TagPill,
} from "@redotech/redo-web/tags/tag-pill";
import { Tooltip } from "@redotech/redo-web/tooltip/tooltip";
import { memo, useLayoutEffect, useRef, useState } from "react";
import * as tagListCss from "./tag-list.module.css";

export const TagList = memo(function TagList({
  tags,
  hoverToShow = false,
  compactFallback = false,
  disableOverflow = false,
}: {
  tags: Tag[];
  hoverToShow?: boolean;
  /** When true, badges that don't fit collapse one-by-one from the right into
   * compact 8x16 colored swatches before falling back to a +N overflow pill.
   * Off by default so non-table callers keep the simple trim-into-+N
   * behavior. */
  compactFallback?: boolean;
  disableOverflow?: boolean;
}) {
  const hiddenRef = useRef<HTMLDivElement>(null);
  const [fullCount, setFullCount] = useState<number>(0);
  const [compactCount, setCompactCount] = useState<number>(0);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [dropdownAnchor, setDropdownAnchor] = useState<HTMLElement | null>(
    null,
  );
  const timeoutRef = useRef<NodeJS.Timeout>();

  useLayoutEffect(() => {
    if (disableOverflow) {
      setFullCount(tags.length);
      setCompactCount(0);
      return;
    }

    if (!hiddenRef.current) {
      return;
    }

    const calculateVisibleTags = () => {
      const parentWidth = hiddenRef.current?.parentElement?.clientWidth || 0;
      const tagElements = Array.from(
        hiddenRef.current?.children[0]?.children || [],
      );
      const fullWidths = tagElements.map((el) => el.clientWidth);
      // Matches Flex gap="md" used in the visible row.
      const gap = 8;
      const compactWidth = 8;
      const overflowPillWidth = 40;
      const n = tags.length;

      const layoutWidth = (full: number, compact: number, hidden: number) => {
        let sum = 0;
        for (let i = 0; i < full; i++) {
          sum += fullWidths[i] ?? 0;
        }
        sum += compact * compactWidth;
        if (hidden > 0) {
          sum += overflowPillWidth;
        }
        const items = full + compact + (hidden > 0 ? 1 : 0);
        if (items > 1) {
          sum += (items - 1) * gap;
        }
        return sum;
      };

      if (compactFallback) {
        // Convert the rightmost full badge to a compact swatch one at a time,
        // until everything fits. Once all are compact and we still overflow,
        // start trimming compact swatches into the +N overflow pill.
        for (let full = n; full >= 0; full--) {
          const compact = n - full;
          if (layoutWidth(full, compact, 0) <= parentWidth) {
            setFullCount(full);
            setCompactCount(compact);
            return;
          }
        }
        for (let compact = n; compact >= 0; compact--) {
          const hidden = n - compact;
          if (layoutWidth(0, compact, hidden) <= parentWidth) {
            setFullCount(0);
            setCompactCount(compact);
            return;
          }
        }
        setFullCount(0);
        setCompactCount(0);
        return;
      }

      // Default: trim full badges into a +N overflow pill (no compact swatches).
      for (let full = n; full >= 0; full--) {
        const hidden = n - full;
        if (layoutWidth(full, 0, hidden) <= parentWidth) {
          setFullCount(full);
          setCompactCount(0);
          return;
        }
      }
      setFullCount(0);
      setCompactCount(0);
    };
    calculateVisibleTags();

    const resizeObserver = new ResizeObserver(calculateVisibleTags);
    const parentElement = hiddenRef.current.parentElement;
    if (parentElement) {
      resizeObserver.observe(parentElement);
    }

    return () => resizeObserver.disconnect();
  }, [tags.length, compactFallback, disableOverflow]);

  return (
    <>
      <span className={tagListCss.hidden} ref={hiddenRef}>
        <Flex gap="md">
          {tags.map((tag) => (
            <TagPill badgeSize="xs" key={tag.name} tag={tag} />
          ))}
        </Flex>
      </span>
      <Flex
        gap="md"
        onMouseEnter={() => {
          timeoutRef.current = setTimeout(() => {
            setIsDropdownOpen(true);
          }, 700);
        }}
        onMouseLeave={() => {
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
          }
          setIsDropdownOpen(false);
        }}
        ref={setDropdownAnchor}
      >
        {tags.slice(0, fullCount).map((tag) => (
          <TagPill badgeSize="xs" key={tag.name} tag={tag} />
        ))}
        {tags.slice(fullCount, fullCount + compactCount).map((tag) => (
          <Tooltip disableInteractive key={tag.name} title={tag.name}>
            {/* Wrap in a span so MUI Tooltip can attach its mouseOver/focus
                  handlers — the swatch is just a styled div. */}
            <span>
              <RedoCompactBadgeSwatch
                color={BadgePillThemeToColorMapping[tag.pillTheme]}
                type={BadgePillThemeToTypeMapping[tag.pillTheme]}
              />
            </span>
          </Tooltip>
        ))}
        {tags.length > fullCount + compactCount && (
          <TagPill
            badgeSize="xs"
            tag={{
              name: `+${tags.length - fullCount - compactCount}`,
              pillTheme: PillTheme.NORMAL,
            }}
          />
        )}
      </Flex>
      {tags.length > fullCount + compactCount && hoverToShow && (
        <Dropdown
          anchor={dropdownAnchor}
          fitToAnchor={false}
          open={isDropdownOpen}
        >
          <Flex className={tagListCss.dropdown} dir="column" gap="md">
            {tags.map((tag) => (
              <TagPill
                key={tag.name}
                tag={{ name: tag.name, pillTheme: PillTheme.NORMAL }}
              />
            ))}
          </Flex>
        </Dropdown>
      )}
    </>
  );
});
